import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { getHelpCenterUrl } from '@sas/nova-core/services';
import { Close, Search } from '@sas/react-svg';
import cx from 'classnames';
import { Button, ButtonVariant } from '../../components/Button';
import { GlobalSettingsKeys, useGetGlobalSetting } from '../../components/Settings';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { getLanguage } from '../../locale/useTextLocalizer';
// import css last
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { CatalogInput } from './__internal__/__CatalogInput/CatalogInput';
import { formatDateRangeForSearch } from './__internal__/borrowed/catalog-app/typescript/src/app/util/format';
import { DateRangeSelectorDialog } from './__internal__/DateRangeSelectorDialog/DateRangeSelectorDialog';
import { SEARCH_DELIMITER } from './__internal__/shared';
import { useCatalogSuggestionsList } from './__internal__/useCatalogSuggestionsList';
import bundle from './NovaComponents-CatalogSearch-gui-icu.properties';
import cn from './CatalogSearch.css.json';
import './CatalogSearch.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const suggestionDisplayOrder = ['help', 'history', 'catalog'];
export const CatalogSearch = /*#__PURE__*/forwardRef(function CatalogSearch({
  onSearch,
  onClearSearch,
  landmarkLabel,
  initialQuery = '',
  enableExperimental = false,
  currentSearchIndices,
  onCurrentSearchIndicesChanged,
  testId,
  placeholder,
  onFocus,
  style,
  className,
  disableHistorySearchOnDoubleClick,
  suggestionsDisplay,
  showClearButton = true,
  clearButtonLabel
}, ref) {
  const localize = useTextLocalizer();
  const [datePickerDialogOpened, setDatePickerDialogOpened] = useState(false);
  const [helpUrls, setHelpUrls] = useState(undefined);
  const searchRef = useRef(null);
  const [searchInputText, setSearchInputText] = useState(initialQuery);
  const [clearButtonVisible, setClearButtonVisible] = useState(false);
  useEffect(() => {
    setSearchInputText(initialQuery);
  }, [initialQuery]);
  const invertColors = useGetGlobalSetting(GlobalSettingsKeys.INVERT_APP_COLORS);
  useEffect(() => {
    if (helpUrls === undefined) {
      const promises = [];

      // Using a Record here so if a new topic is added in the Slice it will break this until a matching entry is added.
      const helpTopics = {
        infocat_search: undefined,
        date_search: undefined,
        freetext_search: undefined,
        keyword_search: undefined,
        nested_search: undefined,
        numeric_search: undefined,
        locale_search: undefined
      };
      for (const k of Object.keys(helpTopics)) {
        const topic = k;
        const searchTopicParams = {
          locale: getLanguage(),
          softwareContextId: topic,
          softwareId: 'SASInformationCatalog'
        };
        const prom = getHelpCenterUrl({
          invertColors: invertColors === 'true',
          customParams: searchTopicParams
        });
        promises.push(prom);
        prom.then(response => {
          helpTopics[topic] = response;
        });
      }
      Promise.all(promises).then(() => {
        setHelpUrls(helpTopics);
      });
    }
  }, [helpUrls, invertColors]);

  // moveToCursorEnd will be either a boolean or a number.  The number will generally be negative and will represent the offset from
  // the end of the inserted suggestion of where to place the cursor.  When the value is true that means the end of the string and when the
  // value is false, that means the cursor is not to be moved
  const [moveCursorToEnd, setMoveCursorToEnd] = useState(false);
  useEffect(() => {
    if (moveCursorToEnd !== false && !!searchInputText) {
      let offset = 0;
      if (moveCursorToEnd !== true) {
        offset = moveCursorToEnd;
      }
      searchRef.current?.setSelectionRange(searchInputText.length + offset, searchInputText.length + offset);
      setMoveCursorToEnd(false);
    }
  }, [searchInputText, moveCursorToEnd]);
  useEffect(() => {
    if (showClearButton) {
      setClearButtonVisible(searchInputText?.length > 0);
    }
  }, [searchInputText, setClearButtonVisible, showClearButton]);
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars({
    'spacing-xl': dt.tokens['space-base-xl']
  }, 'catalog-suggestive-search');
  const executeSearch = useCallback(() => {
    const trimmedSearchText = searchInputText.trim();
    if (trimmedSearchText !== searchInputText) {
      setSearchInputText(trimmedSearchText);
    }
    if (trimmedSearchText) {
      onSearch(trimmedSearchText, currentSearchIndices);
    }
  }, [searchInputText, onSearch, currentSearchIndices]);
  const clearSearch = useCallback(() => {
    setSearchInputText('');
    onClearSearch?.();
    if (searchRef?.current) {
      searchRef.current.focus();
    }
  }, [setSearchInputText, onClearSearch]);

  // aids programmatic search performed using imperative handle.
  const [isProgrammaticQueryBeingExecuted, setProgrammaticQueryExecution] = useState(false);
  useImperativeHandle(ref, () => ({
    doSearch: query => {
      if (searchRef?.current) {
        setProgrammaticQueryExecution(true);
        setSearchInputText(query);
      }
    }
  }), []);
  useEffect(() => {
    if (isProgrammaticQueryBeingExecuted) {
      executeSearch();
      searchRef.current?.focus();
      setProgrammaticQueryExecution(false);
    }
  }, [searchInputText, executeSearch, isProgrammaticQueryBeingExecuted]);
  const selectionStart = searchRef.current?.selectionStart || -1;
  const selectionEnd = searchRef.current?.selectionEnd || -1;
  // Is the cursor somewhere than the end of the current input string?
  // Is some of the input text currently selected?
  // const cursorNotAtEnd: boolean =
  //   (selectionEnd > -1 && selectionEnd < searchInputText.length) || selectionStart !== selectionEnd;

  const suggestionsState = useCatalogSuggestionsList({
    currentText: searchInputText,
    // cursorNotAtEnd ? undefined : searchInputText,
    selectionEndPosition: selectionEnd,
    selectionStartPosition: selectionStart,
    currentSearchIndices,
    enableExperimental,
    suggestionDisplayOrder,
    helpUrls
  });
  const onHistoryItemDoubleClick = useCallback(suggestionEvent => {
    const suggSelectedInfo = suggestionsState?.onSuggestionItemSelected(suggestionEvent);
    if (suggSelectedInfo?.historySelected && suggSelectedInfo.newInputString !== undefined) {
      if (suggSelectedInfo?.historyIndices !== undefined) {
        onCurrentSearchIndicesChanged(suggSelectedInfo.historyIndices);
      }
      setSearchInputText(suggSelectedInfo.newInputString);
      onSearch(suggSelectedInfo.newInputString.trim(), suggSelectedInfo.historyIndices);
    }
  }, [suggestionsState, setSearchInputText, onCurrentSearchIndicesChanged, onSearch]);
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.suggestionSearchBar, className),
    style: {
      ...tokens,
      ...style
    },
    onFocus: e => onFocus?.(e),
    children: [/*#__PURE__*/_jsx(CatalogInput, {
      ref: searchRef,
      className: cx(cn.suggestionSearchInput, showClearButton && cn.hasClearButton),
      suggestionItems: suggestionsState?.suggestions || null,
      onCommit: commitEvent => {
        if (commitEvent.reason === 'enter') {
          executeSearch();
        }
      },
      onHistoryItemDoubleClick: !disableHistorySearchOnDoubleClick ? onHistoryItemDoubleClick : undefined,
      onSuggestionItemSelected: suggestionEvent => {
        const suggSelectedInfo = suggestionsState?.onSuggestionItemSelected(suggestionEvent);
        if (suggSelectedInfo?.historySelected && suggSelectedInfo.newInputString !== undefined) {
          if (suggSelectedInfo?.historyIndices !== undefined) {
            onCurrentSearchIndicesChanged(suggSelectedInfo.historyIndices);
          }
          setSearchInputText(suggSelectedInfo.newInputString);
        } else if (suggSelectedInfo?.datePickerSelected) {
          setDatePickerDialogOpened(true);
        } else if (suggSelectedInfo?.newInputString !== undefined) {
          setSearchInputText(suggSelectedInfo.newInputString);
        }
        setMoveCursorToEnd(suggSelectedInfo?.cursorOffset ?? true);
      },
      onChange: changeEvent => {
        setSearchInputText(changeEvent.value);
      },
      value: searchInputText,
      placeholder: placeholder ?? localize('search.catalog.placeholder.label'),
      landmarkLabel: landmarkLabel ? landmarkLabel : localize('search.generic.landmark.title'),
      testId: postfixTestId(testId, 'cataloginput'),
      tabindex: 0,
      suggestionsDisplay: isProgrammaticQueryBeingExecuted ? 'never' : suggestionsDisplay
    }), /*#__PURE__*/_jsxs("div", {
      className: cn.suggestionSearchButtonContainer,
      children: [clearButtonVisible && /*#__PURE__*/_jsx(Button, {
        className: cn.clearSearchButton,
        variant: ButtonVariant.Icon,
        icon: {
          icon: Close
        },
        onPress: clearSearch,
        tooltip: clearButtonLabel ?? localize('search.clear.label'),
        tokens: () => ({
          fg: dt.tokens['color-body-text'],
          bg: 'transparent',
          bd: 'transparent'
        })
      }), /*#__PURE__*/_jsx(Button, {
        variant: ButtonVariant.Icon,
        icon: {
          icon: Search
        },
        onPress: () => {
          executeSearch();
        },
        tooltip: localize('search.start.label'),
        testId: postfixTestId(testId, 'searchbutton'),
        tokens: () => ({
          fg: dt.tokens['color-body-text'],
          bg: 'transparent',
          bd: 'transparent'
        })
      }), datePickerDialogOpened ? /*#__PURE__*/_jsx(DateRangeSelectorDialog, {
        onOk: (startDate, endDate) => {
          setSearchInputText(searchInputText.substring(0, suggestionsState?.parseInfo?.startIndex).trimLeft() + SEARCH_DELIMITER + formatDateRangeForSearch({
            startDate,
            endDate
          }) + SEARCH_DELIMITER);
          setDatePickerDialogOpened(false);
          setMoveCursorToEnd(true);
        },
        onCancel: () => {
          setDatePickerDialogOpened(false);
        },
        testId: postfixTestId(testId, 'daterange')
      }) : null]
    })]
  });
});
// CatalogSearch.whyDidYouRender = true;
//# sourceMappingURL=CatalogSearch.js.map